"use client";

import { c as _c } from "react/compiler-runtime";
import type { HTMLAttributes, ReactElement, SVGProps } from "react";
import { forwardRef, useCallback, useImperativeHandle, useRef, cloneElement } from "react";
import { m, useAnimation, type Variants } from "framer-motion";
import { cn } from "@/lib/utils";
export interface AnimatedIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}
type AnimationType = "rotate" | "bounce" | "pulse" | "shake" | "flip" | "scale" | "slideRight" | "grow";
interface AnimatedIconProps extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
  icon: ReactElement<SVGProps<SVGSVGElement>>;
  size?: number;
  animation?: AnimationType;
  /** Animate on hover (default: true) */
  animateOnHover?: boolean;
}

// Spring-compatible animations (2 keyframes only)
const animations: Record<AnimationType, Variants> = {
  rotate: {
    normal: {
      rotate: 0
    },
    animate: {
      rotate: 180
    }
  },
  bounce: {
    normal: {
      y: 0
    },
    animate: {
      y: -3
    }
  },
  pulse: {
    normal: {
      scale: 1
    },
    animate: {
      scale: 1.15
    }
  },
  shake: {
    normal: {
      x: 0,
      rotate: 0
    },
    animate: {
      x: 2,
      rotate: 5
    }
  },
  flip: {
    normal: {
      rotateY: 0
    },
    animate: {
      rotateY: 180
    }
  },
  scale: {
    normal: {
      scale: 1
    },
    animate: {
      scale: 1.15
    }
  },
  slideRight: {
    normal: {
      x: 0
    },
    animate: {
      x: 3
    }
  },
  grow: {
    normal: {
      scale: 1,
      y: 0
    },
    animate: {
      scale: 1.1,
      y: -2
    }
  }
};
const AnimatedIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>((t0, ref) => {
  const $ = _c(23);
  const {
    icon,
    onMouseEnter,
    onMouseLeave,
    className,
    size: t1,
    animation: t2,
    animateOnHover: t3
  } = t0;
  const size = t1 === undefined ? 20 : t1;
  const animation = t2 === undefined ? "scale" : t2;
  const animateOnHover = t3 === undefined ? true : t3;
  const controls = useAnimation();
  const isControlledRef = useRef(false);
  let t4;
  if ($[0] !== controls) {
    t4 = () => {
      isControlledRef.current = true;
      return {
        startAnimation: () => controls.start("animate"),
        stopAnimation: () => controls.start("normal")
      };
    };
    $[0] = controls;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  useImperativeHandle(ref, t4);
  let t5;
  if ($[2] !== animateOnHover || $[3] !== controls || $[4] !== onMouseEnter) {
    t5 = e => {
      if (animateOnHover && !isControlledRef.current) {
        controls.start("animate");
      }
      onMouseEnter?.(e);
    };
    $[2] = animateOnHover;
    $[3] = controls;
    $[4] = onMouseEnter;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const handleMouseEnter = t5;
  let t6;
  if ($[6] !== animateOnHover || $[7] !== controls || $[8] !== onMouseLeave) {
    t6 = e_0 => {
      if (animateOnHover && !isControlledRef.current) {
        controls.start("normal");
      }
      onMouseLeave?.(e_0);
    };
    $[6] = animateOnHover;
    $[7] = controls;
    $[8] = onMouseLeave;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const handleMouseLeave = t6;
  let t7;
  if ($[10] !== icon || $[11] !== size) {
    t7 = cloneElement(icon, {
      width: size,
      height: size,
      className: cn("transition-colors", icon.props.className)
    });
    $[10] = icon;
    $[11] = size;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const sizedIcon = t7;
  let t8;
  if ($[13] !== className) {
    t8 = cn("inline-flex items-center justify-center", className);
    $[13] = className;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const t9 = animations[animation];
  let t10;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      type: "spring",
      stiffness: 200,
      damping: 15
    };
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== controls || $[17] !== handleMouseEnter || $[18] !== handleMouseLeave || $[19] !== sizedIcon || $[20] !== t8 || $[21] !== t9) {
    t11 = <m.div className={t8} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} variants={t9} animate={controls} initial="normal" transition={t10}>{sizedIcon}</m.div>;
    $[16] = controls;
    $[17] = handleMouseEnter;
    $[18] = handleMouseLeave;
    $[19] = sizedIcon;
    $[20] = t8;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  return t11;
});
AnimatedIcon.displayName = "AnimatedIcon";
export { AnimatedIcon, type AnimationType };